import type { TelemetryCaptureConfig, TelemetryConfig } from "./types.js";

const DEFAULT_CAPTURE: TelemetryCaptureConfig = {
  llm_generations: true,
  tool_calls: true,
  connector_ops: true,
  mount_ops: true,
  flow_state_changes: true,
  prompt_content: false,
};

/**
 * Baseline {@link TelemetryConfig} used when no `telemetry:` block is present
 * in `skaile.yaml` and no environment variables are set. Provider is `"none"`,
 * so no I/O occurs. All capture flags except `prompt_content` are enabled so
 * that switching to `"otel"` produces useful traces without further config.
 *
 * @docLink packages/telemetry/concepts#configuration
 */
export const DEFAULT_TELEMETRY_CONFIG: TelemetryConfig = {
  provider: "none",
  samplingRate: 1.0,
  batchSize: 100,
  exportIntervalMs: 5000,
  capture: { ...DEFAULT_CAPTURE },
};

/**
 * Resolve telemetry configuration from a partial config object (typically
 * the `telemetry:` section of skaile.yaml) plus environment variable
 * overrides.
 *
 * Precedence (highest first):
 * 1. Environment variables (`SKAILE_TELEMETRY_PROVIDER`, `SKAILE_TELEMETRY_ENDPOINT`,
 *    `LANGFUSE_PUBLIC_KEY`, `LANGFUSE_SECRET_KEY`)
 * 2. Fields in `raw` (from `skaile.yaml`)
 * 3. {@link DEFAULT_TELEMETRY_CONFIG} fallbacks
 *
 * @param raw - Unvalidated object from the `telemetry:` YAML section. Pass `{}`
 *   when no config file is present.
 * @returns A fully-resolved {@link TelemetryConfig} with all fields present.
 * @docLink packages/telemetry/concepts#configuration
 */
/**
 * Dereference an `env:NAME` indirection against `process.env`. A bare value is
 * returned as-is; an `env:`-prefixed value resolves to the named env var (or
 * `undefined` when that var is unset, so the field falls through to its
 * default). Without this, `auth: env:LANGFUSE_PUBLIC_KEY` would be base64'd
 * literally as the string `"env:LANGFUSE_PUBLIC_KEY"`.
 */
function derefEnv(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  if (value.startsWith("env:")) return process.env[value.slice(4)];
  return value;
}

export function resolveTelemetryConfig(raw: Record<string, unknown>): TelemetryConfig {
  const envProvider = process.env.SKAILE_TELEMETRY_PROVIDER;
  const envEndpoint = process.env.SKAILE_TELEMETRY_ENDPOINT;
  const envAuth = process.env.LANGFUSE_PUBLIC_KEY;
  const envSecret = process.env.LANGFUSE_SECRET_KEY;

  const rawCapture = (raw.capture ?? {}) as Partial<TelemetryCaptureConfig>;

  const provider =
    envProvider === "otel" || envProvider === "none"
      ? envProvider
      : raw.provider === "otel"
        ? "otel"
        : DEFAULT_TELEMETRY_CONFIG.provider;

  return {
    provider,
    endpoint:
      envEndpoint ?? (raw.endpoint as string | undefined) ?? DEFAULT_TELEMETRY_CONFIG.endpoint,
    auth: envAuth ?? derefEnv(raw.auth as string | undefined) ?? DEFAULT_TELEMETRY_CONFIG.auth,
    secretKey:
      envSecret ??
      derefEnv(raw.secret_key as string | undefined) ??
      DEFAULT_TELEMETRY_CONFIG.secretKey,
    samplingRate:
      (raw.sampling_rate as number | undefined) ?? DEFAULT_TELEMETRY_CONFIG.samplingRate,
    batchSize: (raw.batch_size as number | undefined) ?? DEFAULT_TELEMETRY_CONFIG.batchSize,
    exportIntervalMs:
      (raw.export_interval_ms as number | undefined) ?? DEFAULT_TELEMETRY_CONFIG.exportIntervalMs,
    capture: {
      ...DEFAULT_CAPTURE,
      ...rawCapture,
    },
  };
}
